import type {
  LeaveAttachmentInput,
  LeaveRequestAttachment,
  LeaveTracking,
  SupportingDocumentRule,
} from './leave-management';
import type { User } from './user';
import { WalletDisplayUnit } from './leave-settings';
import type { LeaveCycleCard } from './leave-cycles';
import type { Employee } from './employee';
import type { GetListResponse } from './generic';
import type { OptionalPageLimit } from './common';

export interface LeaveTypes {
  id: string;
  name: string;
  description: string | undefined;
  quotaAddType: QuotaAddType;
  days: number | undefined;
  quotaStartDay: number | undefined;
  quotaStartDate: string | undefined;
  maxPastDays: number | undefined;
  validFrom: string | undefined;
  validTo: string | undefined;
}

interface LeaveTypeMapping {
  id: string;
  leaveScheduleId: string;
  leaveTypeId: string;
  createdAt: string;
  updatedAt: string;
  leaveType: LeaveTypes;
}

export interface LeaveSchedule {
  id: string;
  name: string;
  fromDate: string | undefined;
  toDate: string | undefined;
  leaveTypeMappings: LeaveTypeMapping[];
}

export interface GetLeaveTypesParams {
  companyId: string | undefined | null;
  page?: number;
  limit?: number;
}

export type GetLeaveTypesResponse = GetListResponse<LeaveTypes>;

export enum QuotaAddType {
  MONTHLY = 'monthly',
  ONE_TIME = 'one_time',
  NONE = 'NONE',
}

export interface LeaveScheduleLeaveTypeMapping {
  id: string;
  leaveSchedule: Pick<LeaveSchedule, 'id' | 'name' | 'fromDate' | 'toDate'>;
  leaveType: LeaveTypes;
}

export interface EmployeeLeaveBalancesResponse {
  message: string;
  data: EmployeeLeaveBalanceItem[];
}

interface EmployeeLeaveBalanceBase {
  id: string;
  totalAllocatedDays: number;
  usedDays: number;
  available: number;
}

export interface EmployeeLeaveBalanceItem extends EmployeeLeaveBalanceBase {
  leaveScheduleLeaveTypeMapping: LeaveScheduleLeaveTypeMapping;
}

interface PolicyLeaveType {
  id: string;
  minAdvanceDays: number;
  isAutoApproved: boolean;
  /** `record_only`: an absence with no balance to show (spec 2026-10-21). */
  tracking?: LeaveTracking;
  isPaid?: boolean;
  reasonRequired?: boolean;
  /** false: an HR-only type, never offered in the employee request form. */
  employeeCanRequest?: boolean;
  supportingDocument?: SupportingDocumentRule;
}

export type FormattedEmployeeLeaveBalances = EmployeeLeaveBalanceBase &
  Pick<LeaveTypes, 'name' | 'description' | 'quotaAddType'> &
  Pick<PolicyLeaveType, 'minAdvanceDays' | 'isAutoApproved'> & {
    policyName: string;
    isActive: boolean;
    tracking?: LeaveTracking;
    isPaid?: boolean;
    reasonRequired?: boolean;
    employeeCanRequest?: boolean;
    supportingDocument?: SupportingDocumentRule;
    walletDisplayUnit?: WalletDisplayUnit;
    totalAllocatedInHours: number;
    takenInHours: number;
    availableInHours: number;
    /** The employee's working hours per day, for hour-based wallets. */
    hoursPerDay?: number;
    /** Leave year of a leave cycles wallet (absent otherwise). */
    leaveCycle?: LeaveCycleCard;
  };

export type WalletActionBaseParams = GetCompanyLeaveRequestByIdParams & {
  walletId: string;
};

export interface GrantWalletBalanceParams extends WalletActionBaseParams {
  amount: number;
  comment?: string;
}

export interface GrantWalletBalanceFormValues {
  amount: string;
  comment: string;
}

export interface DeactivateWalletParams extends WalletActionBaseParams {
  resetWallet: boolean;
}

export interface DeactivateWalletFormValues {
  resetWallet: boolean;
}
export type ActivateWalletParams = WalletActionBaseParams;

export type EmployeeLeaveRequestResponse =
  GetListResponse<EmployeeLeaveRequestItem>;

export enum LeaveRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  AUTO_APPROVED = 'auto_approved',
}

export interface EmployeeLeaveRequestItem extends LeaveRequestItem {
  id: string;
  employeeId: string;
  leaveBalanceId: string;
  /** Supporting documents (signed URLs), 2026-10-21. */
  attachments?: LeaveRequestAttachment[];
  status: LeaveRequestStatus;
  reason: string | null;
  rejectionReason: string | null;
  cancellationReason: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt?: string;

  leaveWalletId?: string;
  leaveWallet?: {
    id: string;
    leaveType: {
      id: string;
      name: string;
    };
  };

  leaveBalance?: {
    id: string;
    leaveScheduleLeaveTypeMapping: {
      id: string;
      leaveType: {
        id: string;
        name: string;
      };
    };
  };
}

export interface CancelledByUser {
  firstName: string;
  lastName: string;
}

export interface LeaveRequestItem {
  days: number;
  startDate: string;
  endDate: string;
  startSession: Meridiem | null;
  endSession: Meridiem | null;
  startTime?: string | null;
  endTime?: string | null;
}

export interface WorkDayMinutesData {
  shiftStart: string;
  shiftEnd: string;
  workDayMinutes: number;
}

export interface WorkDayMinutesResponse {
  message: string;
  data: WorkDayMinutesData;
}

export type EmployeeLeaveRequestListResponse =
  GetListResponse<EmployeeLeaveRequestItem>;

export interface GetEmployeeLeaveRequestsParams
  extends OptionalPageLimit, Partial<DateRangeObj> {
  status?: string | '--';
  /** Leave out requests whose leave ended before today (company time). */
  hidePast?: boolean;
}

interface DateRange {
  fromDate?: string;
  toDate?: string;
}

interface DateRangeObj {
  dateRange: DateRange;
}

export const LEAVE_STATUS_LABEL_MAP: Record<
  string,
  'Pending' | 'Approved' | 'Auto Approved' | 'Rejected' | 'Cancelled'
> = {
  pending: 'Pending',
  approved: 'Approved',
  auto_approved: 'Auto Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export interface LeaveRequestDetailResponse {
  message: 'string';
  data: LeaveRequestDetailItem;
}
interface ApprovedByUser {
  firstName: string;
  lastName: string;
}
interface RejectedByUser {
  firstName: string;
  lastName: string;
}
export interface LeaveRequestDetailItem {
  id: string;
  employeeId: string;
  leaveBalanceId: string;
  status: LeaveRequestStatus;
  reason: string | null;
  startDate: string;
  startSession: Meridiem | null;
  endDate: string;
  endSession: Meridiem | null;
  startTime?: string | null;
  endTime?: string | null;
  days: number;
  rejectionReason: string | null;
  cancellationReason: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  audits: Audit[];
  approvedByUser: ApprovedByUser;
  rejectedByUser: RejectedByUser;
  cancelledByUser: CancelledByUser | null;
  /** Supporting documents (signed URLs), 2026-10-21. */
  attachments?: LeaveRequestAttachment[];

  leaveWalletId?: string;
  leaveWallet?: {
    id: string;
    leaveType: {
      id: string;
      name: string;
    };
  };

  leaveBalance?: {
    id: string;
    employeeId: string;
    leaveScheduleLeaveTypeMappingId: string;
    fromDate: string;
    toDate: string;
    totalAllocatedDays: number;
    usedDays: number;
    createdByUserId: string;
    updatedByUserId: string | null;
    createdAt: string;
    updatedAt: string;

    leaveScheduleLeaveTypeMapping: {
      id: string;
      leaveScheduleId: string;
      leaveTypeId: string;
      createdAt: string;
      updatedAt: string;
      leaveType: LeaveTypes;
    };
  };
}

export interface Audit {
  id: string;
  leaveRequestId: string;
  actionBy: string;
  actionType: LeaveActionType;
  comments: string;
  createdAt: string;
  actionByUser: User;
}

export enum LeaveActionType {
  APPROVE = 'approve',
  REJECT = 'reject',
}

export enum LeaveDuration {
  FULL_DAY = 'fullDay',
  HALF_DAY = 'halfDay',
  MULTI_DAY = 'multiDay',
  HOURLY = 'hourly',
}

export enum Meridiem {
  AM = 'AM',
  PM = 'PM',
}

interface RequestLeaveFormBase {
  leaveBalanceId: string;
  reason: string;
  startDate?: string;
  endDate?: string;
}

export interface LeaveRequestFormValues extends RequestLeaveFormBase {
  meridiem?: Meridiem;
  leaveDate?: string;
  leaveDuration: LeaveDuration;
  startTime?: string;
  endTime?: string;
  /** Supporting documents picked in the form, uploaded on submit. */
  attachments?: File[];
}

export interface AdminLeaveRequestFormValues extends Pick<
  RequestLeaveFormBase,
  'reason' | 'startDate' | 'endDate'
> {
  employeeId: string;
  leaveWalletId: string;
  meridiem?: Meridiem;
  leaveDate?: string;
  leaveDuration: LeaveDuration;
  startTime?: string;
  endTime?: string;
  /** Supporting documents picked in the form, uploaded on submit. */
  attachments?: File[];
}

export interface GetLeaveRequestParams extends OptionalPageLimit {
  companyId: string;
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
}

export type EmployeeLeaveRequestItemWithEmployee = EmployeeLeaveRequestItem & {
  employee: Employee;
};

export type LeaveStatus =
  'pending' | 'approved' | 'rejected' | 'cancelled' | 'all';

export type ApproveLeaveParams = Pick<GetLeaveRequestParams, 'companyId'> & {
  leaveRequestId: string;
};

export interface RejectCompanyLeaveParams extends ApproveLeaveParams {
  rejectionReason: string;
}

export interface CreateLeaveRequestManagementPayload {
  leaveWalletId: string;
  startDate: string;
  endDate: string;
  startSession?: Meridiem;
  endSession?: Meridiem;
  startTime?: string;
  endTime?: string;
  reason: string;
  /** Files already PUT through the upload URL route (max 3). */
  attachments?: LeaveAttachmentInput[];
}

/**
 * The dates of a leave being typed in a request form, sent to the server's
 * day-count endpoint so the preview counts days exactly as a saved request.
 */
export interface LeaveDayCountParams {
  startDate: string;
  endDate: string;
  startSession: Meridiem;
  endSession: Meridiem;
}

/** The server's answer for `LeaveDayCountParams`. */
export interface LeaveDayCount {
  /** Days the request deducts: weekly off days and holidays are free. */
  days: number;
  /** Calendar days from the start to the end date, both included. */
  calendarDays: number;
}

export interface AdminCreateLeaveRequestManagementPayload extends CreateLeaveRequestManagementPayload {
  employeeId: string;
}

export interface GetCompanyLeaveRequestByIdParams {
  companyId: string;
  employeeId: string;
  branchId?: string;
}

export type GetCompanyLeaveBalanceByIdParams =
  GetCompanyLeaveRequestByIdParams & OptionalPageLimit;

export type GetEmployeeLeaveRequestByIdParams =
  GetCompanyLeaveRequestByIdParams &
    OptionalPageLimit & {
      /** Leave out requests whose leave ended before today (company time). */
      hidePast?: boolean;
    };

export interface EmployeeLeavePolicyWalletItem {
  id: string;
  available: number;
  taken: number;
  availableInHours: number;
  takenInHours: number;
  hoursPerDay: number;
  leaveType: {
    id: string;
    name: string;
    description: string | null;
    quotaAddType?: QuotaAddType;
  };
  leavePolicy: {
    id: string;
    name: string;
  };
  policyLeaveType: PolicyLeaveType;
  isActive: boolean;
  walletDisplayUnit?: WalletDisplayUnit;
  /** Leave year of a leave cycles wallet (absent otherwise). */
  leaveCycle?: LeaveCycleCard;
}

export interface EmployeeLeavePolicyWalletResponse {
  message: string;
  data: EmployeeLeavePolicyWalletItem[];
}

export interface DeleteLeaveParams {
  companyId: string;
  id: string;
}

/** Who a direct leave grant goes to. Mirrors the backend GrantRecipientScope. */
export enum GrantRecipientScope {
  All = 'all',
  Branches = 'branches',
  Departments = 'departments',
  Employees = 'employees',
}

/** A direct grant adds to the wallet or deducts from it. Mirrors the backend. */
export enum LeaveGrantType {
  Grant = 'grant',
  Deduct = 'deduct',
}

/** Where a future-dated direct grant stands. Mirrors the backend enum. */
export enum ScheduledGrantStatus {
  Scheduled = 'scheduled',
  Applied = 'applied',
  Cancelled = 'cancelled',
  Failed = 'failed',
}

/** Why a scheduled grant could not be applied on its date. */
export enum ScheduledGrantFailure {
  NoRecipients = 'no_recipients',
  InsufficientBalance = 'insufficient_balance',
  Refused = 'refused',
}

/** How much of a missed yearly lump sum to credit when a policy starts mid-year. */
export enum CatchUpMethod {
  Full = 'full',
  ProRata = 'pro_rata',
}
